/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global window, Promise, FileReader */
import fetch from 'isomorphic-fetch';
import * as Cookies from 'js-cookie';
import React from 'react'; // eslint-disable-line no-unused-vars
import _ from 'underscore';
import moment from 'moment';
import { fromJS, List, Map, Set } from 'immutable';
import { MARKETING_CAMPAIGNS } from './consts/marketingCampaigns';

export function getIn(item, path, defaultItem) {
  if (item === undefined) {
    return defaultItem;
  }
  if (path.length === 0) {
    return item;
  }
  return getIn(item[_.first(path)], _.rest(path), defaultItem);
}

export function capitalize(text) {
  let textToCapitalize;

  if (typeof text === 'object' || typeof text === 'undefined') {
    return '';
  }

  if (typeof text !== 'string') {
    textToCapitalize = text.toString();
  }

  textToCapitalize = text.toString() || '';

  return textToCapitalize
    .charAt(0)
    .toUpperCase()
    .concat(
      textToCapitalize
        .substring(1)
        .toLowerCase(),
    );
}

export function pluralize(count, singular, plural) {
  const countFloat = parseFloat(count);

  if (isNaN(countFloat)) { // eslint-disable-line no-restricted-globals
    throw new Error('The count provided for pluralize function was not a number');
  }

  if (!singular || typeof singular === 'object') {
    throw new Error('Singular provided for pluralize function was empty or incorrect type');
  }

  const pluralForm = plural || singular.toString().concat('s');

  if (!countFloat) {
    return ['No', pluralForm].join(' ');
  } else if (countFloat === 1) {
    return [countFloat, singular].join(' ');
  }

  return [countFloat, pluralForm].join(' ');
}

export const FORMAT_DATE = 'Do MMM YYYY';
export const FORMAT_TIME = 'HH:mm';
export const FORMAT_DATE_TIME = FORMAT_DATE.concat(' [at] ').concat(FORMAT_TIME);
export const FORMAT_DATE_BACKEND = 'YYYY-MM-DD';
export const FORMAT_DATE_WITH_TIMEZONE = 'YYYY-MM-DD:ZZ';
export const FORMAT_DATE_TIME_BACKEND = FORMAT_DATE_BACKEND.concat('THH:mm:ss');
export const FORMAT_DATE_TIME_ADMIN = FORMAT_DATE_BACKEND.concat(' ').concat(FORMAT_TIME);
export const FORMAT_DATE_REPORTS = 'MMM D, YYYY';

export function formatDateTime(date, noDate, format = FORMAT_DATE_TIME) {
  if (!date) {
    return noDate || 'unknown';
  }
  return moment(date).format(format);
}

export function formatDate(date) {
  return moment(date).format(FORMAT_DATE);
}

export function formatBackendDate(date) {
  return moment(date).format(FORMAT_DATE_BACKEND);
}

export function formatBackendDateTime(date, time) {
  return moment(`${date} ${time}`).format(FORMAT_DATE_TIME_BACKEND);
}

export function formatTime(datetime) {
  return moment(datetime).format(FORMAT_TIME);
}

export function formatTimeNoDate(time) {
  return moment(time, 'HH:mm:ss').format('ha');
}

export function padTens(number) {
  return (number < 10 ? '0' : '') + number;
}

export function compare(a, b) {
  if (a > b) { return 1; }
  if (a < b) { return -1; }
  return 0;
}

export function sort(items, transform, ascending) {
  return items.sort(
    (item1, item2) => compare(transform(item1), transform(item2)) * (ascending ? 1 : -1),
  );
}

export function toQueryString(data) {
  return data
    ? _.pairs(data)
      .filter(([key, value]) => key && value)
      .map(pair => pair.map(encodeURIComponent))
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
    : '';
}

export function fromQueryString(queryString) {
  return queryString ?
    _.object(
      queryString
        .split('&')
        .map(keyValue => keyValue.split('=').map(decodeURIComponent)),
    )
    : {};
}

// Append these to datetime fields to add comparisons to a filter
export const COMPARATOR_SUFFIXES = { before: '_before', after: '_after', on: '_on' };

export function momentRangeBounded(range, value) {
  const { start, end } = _.mapObject(range, each => each && moment(each));

  if (start && value.isBefore(start)) {
    return start;
  }
  if (end && value.isAfter(end)) {
    return end;
  }

  return value;
}

export function toProperCase(string) {
  if (!string || !string.length) {
    return '';
  }

  return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

export function startsWith(haystack, needle) {
  return haystack.lastIndexOf(needle, 0) === 0;
}

export function getDefault(value, defaultValue) {
  return typeof value === 'undefined' ? defaultValue : value;
}

export function getOrdinal(number) {
  if (!isNaN(number) && parseFloat(number) === parseInt(number, 10)) { // eslint-disable-line no-restricted-globals, max-len
    const strings = ['th', 'st', 'nd', 'rd'];
    const v = number % 100;
    return number + (strings[(v - 20) % 10] || strings[v] || strings[0]);
  }
  return number;
}

export function parseImmutablePropertyString(obj, stringFormat) {
  return stringFormat.replace(/\$\{(.*?)\}/gi, (property, propertyName) => obj.getIn(propertyName.split('.'), '--'));
}

export function rangeBounded(value, max) {
  return (value + max) % max;
}

export function fraction(collection, predicate) {
  return collection.filter(predicate).size / (collection.size || 1);
}

export function asPercent(_fraction) {
  return Math.round(100 * _fraction);
}

export function isValidUUID(value) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return value && uuidRegex.test(value);
}

// recursively merge - priority given to properties on source
export function deepMergeObject(targetObject, sourceObject) {
  const target = fromJS(targetObject);
  const source = fromJS(sourceObject);
  return target.mergeDeep(source).toJS();
}

// like interleave but keep going until both lists are exhausted
export function interweave(list1, list2) {
  const [longest, shortest] = list1.size > list2.size ? [list1, list2] : [list2, list1];

  return list1.interleave(list2).concat(longest.slice(shortest.size));
}

export const formatDateOrNull = (date, format, nullRepresentation = null) =>
  (date ? date.format(format) : nullRepresentation);

function removeUndefineds(data) {
  return _.chain(data)
    .pairs()
    .filter(([/* key */, value]) => value !== undefined)
    .object()
    .value();
}

const isFile = object => object.constructor === window.File;
const isFileList = object => object.constructor === window.FileList;
const isFileLike = object => isFile(object) || isFileList(object);

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = evt => resolve(evt.target.result);
    reader.onerror = reject;
  });
}

function transformFileFieldToBase64(fieldData) {
  if (fieldData && isFileLike(fieldData)) {
    const file = isFileList(fieldData) ? fieldData[0] : fieldData;
    return file
      ? getBase64(file)
      : Promise.resolve(undefined);
  }

  return Promise.resolve(fieldData);
}

export function encodeFiles(data) {
  const fieldsToBeResolved = Object.keys(data)
    .map(fieldName => transformFileFieldToBase64(data[fieldName]));

  return Promise.all(fieldsToBeResolved)
    .then(resolvedFields => _.object(Object.keys(data), resolvedFields));
}

export function getCallSpec(url, method, optionalData) {
  const callSpec = {
    endpoint: url,
    headers: {
      Accept: 'application/json',
    },
    credentials: 'same-origin',
  };

  const csrf = Cookies.get('csrftoken');
  if (csrf) {
    callSpec.headers['X-CSRFToken'] = csrf;
  }

  if (optionalData) {
    // If object, remove items where value is 'undefined'
    const data = _.isArray(optionalData) ? optionalData : removeUndefineds(optionalData);

    if (method === 'GET') {
      const queryString = _.map(
        data,
        (value, key) => [key, value].map(encodeURIComponent).join('='),
      ).join('&');

      if (queryString) {
        callSpec.endpoint += `?${queryString}`;
      }
    } else if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
      callSpec.headers['Content-Type'] = 'application/json';
      callSpec.body = JSON.stringify(data);
    }
  }

  return callSpec;
}

export function apiRequest(url, method, data) {
  const { endpoint, headers, credentials, body } = getCallSpec(url, method, data);
  return fetch(endpoint, { method, headers, credentials, body });
}

export function objectWithoutProperties(obj, keys) {
  const target = {};
  for (const i in obj) { // eslint-disable-line no-restricted-syntax
    if (keys.indexOf(i) >= 0) continue; // eslint-disable-line no-continue
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; // eslint-disable-line no-continue
    target[i] = obj[i];
  }
  return target;
}

export const isImmutable = maybeImmutable =>
  List.isList(maybeImmutable) || Map.isMap(maybeImmutable) || Set.isSet(maybeImmutable);

export function transformValuesToStrings(object) {
  return object.map((value) => {
    if (isImmutable(value)) {
      return transformValuesToStrings(value);
    }
    if (value !== null && value !== undefined) {
      return value.toString();
    }
    return value;
  });
}

// Transform { foos_x1: true, foos_y1: true } into { foos: ['x1', 'y1'] }
export function transformBooleansToArray(data, key) {
  const matchesKey = (value, key_) => key_.startsWith(`${key}_`);
  const values = data
    .filter(matchesKey)
    .filter(value => value)
    .keySeq()
    .map(key_ => key_.slice(`${key}_`.length));
  return data.filterNot(matchesKey).set(key, values);
}

// Transform { foos: ['x1', 'y1'] } into { foos_x1: true, foos_y1: true }
export function transformArrayToBooleans(data, key) {
  const keys = Map(data.get(key, []).map(eachValue => [`${key}_${eachValue}`, true]));
  return data.delete(key).merge(keys);
}

export const resolveWith = value =>
  new Promise(resolve => resolve(value));

/**
 * @param {string} s
 * @returns {string}
 */
export const reverse = s => s.split('').reverse().join('');

/**
 * @param {string} separator
 * @param {string} s
 * @returns {Array}
 */
export const separateByRightmost = (separator, s) => {
  const afterSeparator = reverse(s).split(separator, 1).reverse();
  return [
    s.substr(0, s.length - afterSeparator[0].length - separator.length),
    reverse(afterSeparator[0]),
  ];
};

const withoutOwnRole = (data, loggedOnUser) =>
  fromJS(data)
    .update(
      'users',
      users =>
        users && users.map(
          user => (
            user.get('id') === loggedOnUser.get('id')
              ? user.delete('role')
              : user
          ),
        ),
    ).toJS();

export const transformCampaignFormData = (data, loggedOnUser = null) => {
  const baseData = loggedOnUser ? withoutOwnRole(data, loggedOnUser) : { ...data };

  // Create an array of all marketing campaigns
  const ALL_MARKETING_CAMPAIGNS = Object.values(MARKETING_CAMPAIGNS);

  const formData = { ...baseData };
  formData.default_disabled_campaigns = ALL_MARKETING_CAMPAIGNS.filter(
    campaign => !data[campaign]
  );

  // Remove the individual checkbox fields since they're not part of the Office Model
  ALL_MARKETING_CAMPAIGNS.forEach(campaign => {
    delete formData[campaign];
  });

  return formData;
}
